import { el, clear } from '../lib/dom.js';
import { getItem, getItemFlingEffect } from '../data/api.js';
import { loadSnapshot } from '../data/snapshot.js';
import { prettify } from '../data/pokedex-resolver.js';
import { settings } from '../store/settings.js';
import { Sprite } from '../components/Sprite.js';

const ITEM_SPRITE = (name) =>
  `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${name}.png`;
const idFromUrl = (u) => Number(u.replace(/\/$/, '').split('/').pop());

const english = (entries, key) => {
  const hit = (entries || []).find((e) => e.language.name === 'en');
  return hit ? hit[key].replace(/\s+/g, ' ').trim() : '';
};

function fact(k, v) {
  return el('div', { class: 'fact' }, el('dt', {}, k), el('dd', {}, String(v)));
}

export async function ItemDetail(id) {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading item…'));

  const snap = await loadSnapshot();

  let d;
  try {
    d = await getItem(id);
  } catch (err) {
    clear(view).append(
      el(
        'div',
        { class: 'view--error' },
        el('h1', {}, 'Could not load this item'),
        el('p', {}, navigator.onLine ? err.message : 'You appear to be offline.'),
        el('a', { class: 'button', href: '#/search' }, 'Back to lookup'),
      ),
    );
    return view;
  }

  const effect =
    english(d.effect_entries, 'short_effect') ||
    english(d.flavor_text_entries, 'text') ||
    'No description.';

  // What happens to the target when this item is used with the move Fling —
  // a separate resource from the item's own effect text, only worth the
  // extra round trip when the item actually has one.
  const flingEffect = d.fling_effect
    ? await getItemFlingEffect(d.fling_effect.name)
        .then((fe) => english(fe.effect_entries, 'effect'))
        .catch(() => '')
    : '';

  const img = el('img', { class: 'lookup__item-img', alt: d.name, src: ITEM_SPRITE(d.name) });
  img.addEventListener('error', () => img.remove(), { once: true });

  // Pokémon that hold this item in the wild. held_by_pokemon carries a
  // per-version breakdown, so for a specific game we can filter exactly.
  const vg = settings.get().versionGroup;
  const versions = new Set(snap.versionGroupByKey.get(vg)?.versions || []);
  const holders = (d.held_by_pokemon || [])
    .filter(
      (h) =>
        vg === 'all' ||
        !versions.size ||
        (h.version_details || []).some((vd) => versions.has(vd.version.name)),
    )
    .map((h) => snap.speciesById.get(idFromUrl(h.pokemon.url)))
    .filter(Boolean)
    .sort((a, b) => a.id - b.id);

  const style = settings.get().spriteStyle;
  const HOLDER_CAP = 400;
  const holdersGrid = el('div', { class: 'ab-mons' });
  function paintHolders(q) {
    const qq = (q || '').trim().toLowerCase().replace(/\s+/g, '-');
    const shown = qq ? holders.filter((sp) => sp.name.includes(qq)) : holders;
    clear(holdersGrid);
    if (!shown.length) {
      holdersGrid.append(el('p', { class: 'detail__muted' }, 'No matches.'));
      return;
    }
    for (const sp of shown.slice(0, HOLDER_CAP)) {
      holdersGrid.append(
        el(
          'a',
          { class: 'ab-mon', href: `#/pokemon/${sp.id}` },
          Sprite(sp.id, { style, alt: sp.name, size: 48 }),
          el('span', {}, sp.name.replace(/-/g, ' ')),
        ),
      );
    }
    if (shown.length > HOLDER_CAP) {
      holdersGrid.append(
        el(
          'p',
          { class: 'detail__muted' },
          `+${shown.length - HOLDER_CAP} more — filter to narrow.`,
        ),
      );
    }
  }
  paintHolders('');
  const holderFilter = el('input', {
    type: 'search',
    class: 'search__input learners__filter',
    placeholder: 'Filter Pokémon…',
    oninput: (e) => paintHolders(e.target.value),
  });

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/search' }, '‹ Lookup')),
    el(
      'header',
      { class: 'lookup__head lookup__head--item' },
      img,
      el(
        'div',
        {},
        el('h1', {}, d.name.replace(/-/g, ' ')),
        el('span', { class: 'lookup__class' }, prettify(d.category?.name || 'item')),
      ),
    ),
    el(
      'dl',
      { class: 'pfacts' },
      d.cost ? fact('Buy price', `₽${d.cost.toLocaleString()}`) : null,
      d.fling_power != null ? fact('Fling power', d.fling_power) : null,
      flingEffect ? fact('Fling effect', flingEffect) : null,
      d.attributes?.length
        ? fact('Attributes', d.attributes.map((a) => prettify(a.name)).join(', '))
        : null,
      fact('Held by', `${holders.length} Pokémon`),
    ),
    el('section', { class: 'detail__section' }, el('h2', {}, 'Effect'), el('p', {}, effect)),
    // Spread, not a bare `? … : null` arg: clear(view).append(...) below is the
    // native Element.append(), which stringifies null/undefined args into a
    // literal "null" text node instead of skipping them like el()'s children do.
    ...(holders.length
      ? [
          el(
            'section',
            { class: 'detail__section' },
            el(
              'h2',
              {},
              `Held in the wild by${vg === 'all' ? '' : ` in ${prettify(vg)}`} (${holders.length})`,
            ),
            holders.length > 24 ? holderFilter : null,
            holdersGrid,
          ),
        ]
      : []),
  );
  return view;
}
